// =============================================
// PipelineConfigPanel — batch size + parallel config per step
// + Worker concurrency (global, from worker_config table)
// =============================================

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Settings2, Save, Cpu, Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePipelineConfig } from "@/hooks/usePipelineConfig";
import { useWorkerConfig } from "@/hooks/useWorkerConfig";

export function PipelineConfigPanel() {
  const { configs, loading, updateConfig, stepLabels } = usePipelineConfig();
  const {
    concurrency,
    loading: workerLoading,
    saveConcurrency,
  } = useWorkerConfig();
  const [workerEdit, setWorkerEdit] = useState<number | null>(null);
  const [edits, setEdits] = useState<Record<string, { batch_size: number; parallel_batches: number }>>({});

  const workerValue = workerEdit ?? concurrency.value;
  const workerChanged = workerEdit !== null && workerEdit !== concurrency.value;
  const clampWorker = (v: number) =>
    Math.max(concurrency.min, Math.min(concurrency.max, Math.floor(v || concurrency.value)));

  const getEdited = (step: string) => {
    const config = configs.find((c) => c.step === step);
    return edits[step] || { batch_size: config?.batch_size || 50, parallel_batches: config?.parallel_batches || 3 };
  };

  const isChanged = (step: string) => {
    const config = configs.find((c) => c.step === step);
    const edited = edits[step];
    if (!edited || !config) return false;
    return edited.batch_size !== config.batch_size || edited.parallel_batches !== config.parallel_batches;
  };

  const handleSave = async (step: string) => {
    const edited = edits[step];
    if (!edited) return;
    await updateConfig(step, edited.batch_size, edited.parallel_batches);
    setEdits((prev) => {
      const next = { ...prev };
      delete next[step];
      return next;
    });
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground py-4">Ładowanie konfiguracji...</div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold flex items-center gap-2 mb-1">
          <Settings2 className="h-5 w-5" />
          Konfiguracja pipeline
        </h3>
        <p className="text-sm text-muted-foreground">
          2 osobne ustawienia: <strong>Worker</strong> (ile JOBÓW worker bierze z kolejki naraz) i <strong>per-etap</strong> (ile API CALLI Claude/OpenAI w obrębie 1 batcha).
        </p>
      </div>

      {/* ============================================= */}
      {/* SEKCJA 1: Worker (global, BullMQ poziom)      */}
      {/* ============================================= */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 border-b pb-1">
          <Cpu className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          <h4 className="text-xs font-semibold uppercase tracking-wide text-purple-600 dark:text-purple-400">
            1. Worker (globalne — ile JOBÓW BullMQ równolegle)
          </h4>
        </div>
        <div className="border-2 border-purple-500/40 bg-purple-500/5 rounded-lg p-4 flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px] flex items-start gap-2">
            <Cpu className="h-4 w-4 text-purple-600 dark:text-purple-400 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-sm flex items-center gap-2">
                Worker — joby równolegle z kolejki
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-xs">
                      Ile JOBÓW (cale zadania typu "wygeneruj encje dla mapy X") worker
                      pobiera z BullMQ Redis JEDNOCZEŚNIE. NIE myl z parallel_batches niżej —
                      to jest <strong>ile zadań na raz</strong>, nie ile API calls w obrębie zadania.
                      Limiter 50/min OpenAI globalnie. Zmiana wymaga
                      <strong> Redeploy Worker </strong> w Coolify (NIE hot-reload).
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </p>
              <p className="text-xs text-muted-foreground">
                worker_config.concurrency (BullMQ worker level, bounds [{concurrency.min}, {concurrency.max}])
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs whitespace-nowrap">Joby równolegle:</Label>
            <Input
              type="number"
              min={concurrency.min}
              max={concurrency.max}
              step={1}
              value={workerValue}
              onChange={(e) => setWorkerEdit(clampWorker(parseInt(e.target.value, 10)))}
              disabled={workerLoading}
              className="w-20 h-8 text-sm"
            />
          </div>
          {workerChanged ? (
            <Button
              size="sm"
              className="h-8"
              onClick={async () => {
                await saveConcurrency(workerValue);
                setWorkerEdit(null);
              }}
            >
              <Save className="h-3.5 w-3.5 mr-1" />
              Zapisz
            </Button>
          ) : (
            <Badge variant="outline" className="text-xs h-8 px-3 border-purple-500/40">
              {workerLoading ? "…" : `${concurrency.value}`}
            </Badge>
          )}
        </div>
      </div>

      {/* ============================================= */}
      {/* SEKCJA 2: Per-etap (w obrębie 1 joba)         */}
      {/* ============================================= */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 border-b pb-1">
          <Settings2 className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            2. Per-etap (w obrębie 1 joba — ile CALLI Claude/OpenAI równolegle)
          </h4>
        </div>
        <div className="space-y-3">
        {configs.map((config) => {
          const edited = getEdited(config.step);
          const changed = isChanged(config.step);

          return (
            <div
              key={config.step}
              className="border rounded-lg p-4 flex items-center gap-4 flex-wrap"
            >
              {/* Step name */}
              <div className="flex-1 min-w-[200px]">
                <p className="font-medium text-sm">{stepLabels[config.step] || config.step}</p>
                <p className="text-xs text-muted-foreground">{config.step}</p>
              </div>

              {/* Batch size */}
              <div className="flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap">Batch size:</Label>
                <Input
                  type="number"
                  min={5}
                  max={200}
                  value={edited.batch_size}
                  onChange={(e) =>
                    setEdits((prev) => ({
                      ...prev,
                      [config.step]: { ...edited, batch_size: parseInt(e.target.value) || 50 },
                    }))
                  }
                  className="w-20 h-8 text-sm"
                />
              </div>

              {/* Parallel API calls (w obrebie 1 joba — to JEST CO INNEGO niz Worker concurrency wyzej!) */}
              <div className="flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap">Calle API równolegle:</Label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={edited.parallel_batches}
                  onChange={(e) =>
                    setEdits((prev) => ({
                      ...prev,
                      [config.step]: { ...edited, parallel_batches: parseInt(e.target.value) || 3 },
                    }))
                  }
                  className="w-16 h-8 text-sm"
                />
              </div>

              {/* Status */}
              {changed ? (
                <Button size="sm" className="h-8" onClick={() => handleSave(config.step)}>
                  <Save className="h-3.5 w-3.5 mr-1" />
                  Zapisz
                </Button>
              ) : (
                <Badge variant="outline" className="text-xs h-8 px-3">
                  {config.batch_size} × {config.parallel_batches}
                </Badge>
              )}
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
}
